import { useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';

import type { InlineCommentTopLevelMarkerRefsType } from '@confluence/inline-comments-queries';
import { InlineCommentTopLevelMarkerRefs } from '@confluence/inline-comments-queries';
import {
	updateCommentHighlightsInEditor,
	getEditorResolvedState,
	getSortedMarkerRefs,
} from '@confluence/comments-util';

type CommentRefetchProps = {
	isInitialRender: boolean;
	setIsInitialRender: React.Dispatch<React.SetStateAction<boolean>>;
	isDocumentActive: boolean;
	contentId: string;
	triggerAnalyticsEvent: (
		eventName: string,
		extraData?: {
			[key: string]: any;
		},
	) => Promise<void>;
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
	handleGqlError: (error: Error) => void;
	previousAnnotations: string[];
	updateUnreadCommentsOnFirstRender: (shouldUpdateUnreadCommentsOnFirstRender: boolean) => void;
	updateUnreadComments: (shouldUpdateUnreadComments: boolean) => void;
};

export const useCommentRefetch = ({
	isInitialRender,
	setIsInitialRender,
	isDocumentActive,
	contentId,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
	handleGqlError,
	previousAnnotations,
	updateUnreadCommentsOnFirstRender,
	updateUnreadComments,
}: CommentRefetchProps) => {
	const { refetch } = useQuery<InlineCommentTopLevelMarkerRefsType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		InlineCommentTopLevelMarkerRefs,
		{
			variables: {
				pageId: contentId,
				contentStatus: ['DRAFT', 'CURRENT'],
			},
			skip: true,
			onError: handleGqlError,
			fetchPolicy: 'network-only',
		},
	);

	useEffect(() => {
		if (isInitialRender) {
			setIsInitialRender(false);
		} else if (isDocumentActive) {
			refetch({
				depth: 'ROOT',
				type: ['UNRESOLVED'],
			})
				.then((response) => {
					const updatedAnnotations = updateCommentHighlightsInEditor({
						data: response.data,
						previousAnnotations,
					});

					// organizes the annotations for navigation to work
					if (updatedAnnotations.length) {
						void getEditorResolvedState({
							annotations: getSortedMarkerRefs(updatedAnnotations, 'edit'),
							pageId: contentId,
							triggerAnalyticsEvent,
							updateUnresolvedInlineComment,
						});

						updateUnreadCommentsOnFirstRender(true);
						updateUnreadComments(true);
					}
				})
				.catch((error) => {
					handleGqlError(error);
				});
		}
		// we only want to update the comment highlights when the user comes back to the page
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInitialRender, isDocumentActive]);
};
